import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Download, Edit } from '@mui/icons-material';
import { DashboardLayout } from '../../components/DashboardLayout';
import { PageHint, IDocKey } from '../../components/common/PageHint';
import { PageCard } from '../../components/common/PageCard';
import { AdaptiveTabs, AdaptiveTabItem } from '../../components/common/AdaptiveTabs';
import { ConfirmationDialog } from '../../components/common/ConfirmationDialog';
import { toastService } from '../../services/toast.service';
import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import { questionnairesConstructionService, QuestionnairesConstructionService } from '../../services/questionnaires-construction/questionnaires-construction.service';
import {
  ConstructionOption,
  ConstructionQuestion,
  ConstructionStep,
  ConstructionTemplateDetail,
  CreateOptionDto,
  CreateQuestionDto,
  CreateStepDto,
  QuestionType,
} from '../../types/questionnaire-construction';
import { StepFormDialog } from '../../components/quiz-builder/StepFormDialog';
import { QuestionFormDialog } from '../../components/quiz-builder/QuestionFormDialog';
import { OptionFormDialog } from '../../components/quiz-builder/OptionFormDialog';
import { QuestionCard } from '../../components/quiz-builder/QuestionCard';

type DeleteTarget =
  | { kind: 'step'; id: number; label: string }
  | { kind: 'question'; id: number; label: string }
  | { kind: 'option'; id: number; label: string };

export interface QuizBuilderEditorContentProps {
  service: QuestionnairesConstructionService;
  iDoc: IDocKey;
  titlePrefix: string;
  /** Адмін редагує абсолютно всі шаблони, включно з системними — readOnly для них не діє. */
  isAdminMode?: boolean;
}

export const QuizBuilderEditorContent: React.FC<QuizBuilderEditorContentProps> = ({
  service,
  iDoc,
  titlePrefix,
  isAdminMode = false,
}) => {
  const { templateId } = useParams<{ templateId: string }>();
  const id = Number(templateId);

  const [template, setTemplate] = useState<ConstructionTemplateDetail | null>(null);
  const [questionTypes, setQuestionTypes] = useState<QuestionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeStepId, setActiveStepId] = useState<string>('');

  const [meta, setMeta] = useState({ name: '', description: '', welcomeNotes: '' });
  const [savingMeta, setSavingMeta] = useState(false);

  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<ConstructionStep | null>(null);

  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<ConstructionQuestion | null>(null);

  const [optionDialogOpen, setOptionDialogOpen] = useState(false);
  const [optionTarget, setOptionTarget] = useState<{ questionId: number; option: ConstructionOption | null } | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tpl, opts] = await Promise.all([
        service.getTemplate(id),
        service.getOptions(),
      ]);
      setTemplate(tpl);
      setQuestionTypes(opts.questionTypes);
      setMeta({
        name: tpl.nestNameI18n?.uk ?? '',
        description: tpl.nestDescriptionI18n?.uk ?? '',
        welcomeNotes: tpl.nestWelcomeNotesI18n?.uk ?? '',
      });
      setActiveStepId(prev => {
        if (prev && tpl.steps.some(s => String(s.id) === prev)) return prev;
        return tpl.steps[0] ? String(tpl.steps[0].id) : '';
      });
      setError(null);
    } catch (err: any) {
      setError(err?.response?.status === 404 ? 'Опитувальник не знайдено.' : 'Не вдалося завантажити опитувальник.');
    } finally {
      setLoading(false);
    }
  }, [id, service]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const titleOverride = template
    ? `${titlePrefix} → ${template.nestNameI18n?.uk || `Опитувальник #${template.id}`}`
    : titlePrefix;

  if (loading && !template) {
    return (
      <DashboardLayout titleOverride={titleOverride}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout titleOverride={titleOverride}>
        <Alert severity="error">{error}</Alert>
      </DashboardLayout>
    );
  }

  if (!template) {
    return <DashboardLayout titleOverride={titleOverride}>{null}</DashboardLayout>;
  }

  const readOnly = !isAdminMode && template.isSystem;
  const activeStep = template.steps.find(s => String(s.id) === activeStepId) ?? null;
  const stepTabs: AdaptiveTabItem[] = template.steps.map(s => ({
    value: String(s.id),
    label: s.nestCaptionI18n?.uk || `Крок ${s.order}`,
  }));

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await service.downloadBlankFormPdf(id, meta.name);
    } catch {
      toastService.error('Не вдалося завантажити PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleSaveMeta = async () => {
    setSavingMeta(true);
    try {
      await service.updateTemplate(id, {
        nestNameI18n: { uk: meta.name },
        nestDescriptionI18n: { uk: meta.description },
        nestWelcomeNotesI18n: { uk: meta.welcomeNotes },
      });
      toastService.success('Збережено');
      await fetchAll();
    } catch {
      toastService.error('Не вдалося зберегти');
    } finally {
      setSavingMeta(false);
    }
  };

  const handleOpenAddStep = () => { setEditingStep(null); setStepDialogOpen(true); };
  const handleOpenEditStep = (step: ConstructionStep) => { setEditingStep(step); setStepDialogOpen(true); };

  const handleSaveStep = async (dto: CreateStepDto) => {
    try {
      if (editingStep) {
        await service.updateStep(editingStep.id, dto);
        toastService.success('Крок оновлено');
      } else {
        await service.createStep(id, dto);
        toastService.success('Крок додано');
      }
      setStepDialogOpen(false);
      await fetchAll();
    } catch {
      toastService.error('Не вдалося зберегти крок');
    }
  };

  const handleOpenAddQuestion = () => { setEditingQuestion(null); setQuestionDialogOpen(true); };
  const handleOpenEditQuestion = (q: ConstructionQuestion) => { setEditingQuestion(q); setQuestionDialogOpen(true); };

  const handleSaveQuestion = async (dto: CreateQuestionDto) => {
    if (!activeStep) return;
    try {
      if (editingQuestion) {
        await service.updateQuestion(editingQuestion.id, dto);
        toastService.success('Питання оновлено');
      } else {
        await service.createQuestion(activeStep.id, dto);
        toastService.success('Питання додано');
      }
      setQuestionDialogOpen(false);
      await fetchAll();
    } catch {
      toastService.error('Не вдалося зберегти питання');
    }
  };

  const handleOpenAddOption = (questionId: number) => { setOptionTarget({ questionId, option: null }); setOptionDialogOpen(true); };
  const handleOpenEditOption = (question: ConstructionQuestion, optionId: number) => {
    const option = question.options.find(o => o.id === optionId) ?? null;
    setOptionTarget({ questionId: question.id, option });
    setOptionDialogOpen(true);
  };

  const handleSaveOption = async (dto: CreateOptionDto) => {
    if (!optionTarget) return;
    try {
      if (optionTarget.option) {
        await service.updateOption(optionTarget.option.id, dto);
        toastService.success('Варіант оновлено');
      } else {
        await service.createOption(optionTarget.questionId, dto);
        toastService.success('Варіант додано');
      }
      setOptionDialogOpen(false);
      await fetchAll();
    } catch {
      toastService.error('Не вдалося зберегти варіант');
    }
  };

  const handleMoveQuestion = async (question: ConstructionQuestion, direction: 'up' | 'down') => {
    if (!activeStep) return;
    const list = activeStep.questions;
    const index = list.findIndex(q => q.id === question.id);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (index === -1 || swapIndex < 0 || swapIndex >= list.length) return;
    const other = list[swapIndex];
    try {
      await service.updateQuestion(question.id, { order: other.order });
      await service.updateQuestion(other.id, { order: question.order });
      await fetchAll();
    } catch {
      toastService.error('Не вдалося змінити порядок питань');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.kind === 'step') await service.deleteStep(deleteTarget.id);
      if (deleteTarget.kind === 'question') await service.deleteQuestion(deleteTarget.id);
      if (deleteTarget.kind === 'option') await service.deleteOption(deleteTarget.id);
      toastService.success('Видалено');
      setDeleteTarget(null);
      await fetchAll();
    } catch {
      toastService.error('Не вдалося видалити');
    }
  };

  const nextStepOrder = template.steps.length > 0 ? Math.max(...template.steps.map(s => s.order)) + 1 : 1;
  const nextQuestionOrder = activeStep && activeStep.questions.length > 0
    ? Math.max(...activeStep.questions.map(q => q.order)) + 1
    : 1;
  const editingOptionQuestion = optionTarget ? activeStep?.questions.find(q => q.id === optionTarget.questionId) : undefined;
  const nextOptionOrder = editingOptionQuestion && editingOptionQuestion.options.length > 0
    ? Math.max(...editingOptionQuestion.options.map(o => o.order)) + 1
    : 1;

  return (
    <DashboardLayout titleOverride={titleOverride}>
    <Box data-i-doc={iDoc}>
      <PageHint iDoc={iDoc} />

      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Це системний опитувальник — лише перегляд, редагування недоступне.
        </Alert>
      )}
      {isAdminMode && template.isSystem && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Це системний опитувальник — видимий усім клієнтам за замовчуванням.
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={downloadingPdf ? <CircularProgress size={16} /> : <Download />}
          onClick={handleDownloadPdf}
          disabled={downloadingPdf}
        >
          Завантажити порожній бланк (PDF)
        </Button>
      </Box>

      <PageCard>
        <Box display="flex" flexDirection="column" gap={2} sx={{ mb: 2 }}>
          <TextField
            label="Назва опитувальника"
            value={meta.name}
            onChange={(e) => setMeta(m => ({ ...m, name: e.target.value }))}
            fullWidth
            size="small"
            disabled={readOnly}
          />
          <TextField
            label="Опис"
            value={meta.description}
            onChange={(e) => setMeta(m => ({ ...m, description: e.target.value }))}
            fullWidth
            size="small"
            disabled={readOnly}
          />
          <TextField
            label="Вітальний текст"
            value={meta.welcomeNotes}
            onChange={(e) => setMeta(m => ({ ...m, welcomeNotes: e.target.value }))}
            fullWidth
            size="small"
            disabled={readOnly}
          />
          {!readOnly && (
            <Box>
              <Button
                variant="outlined"
                onClick={handleSaveMeta}
                disabled={savingMeta}
                startIcon={savingMeta ? <CircularProgress size={16} /> : undefined}
              >
                Зберегти назву й опис
              </Button>
            </Box>
          )}
        </Box>
      </PageCard>

      <PageCard sx={{ mt: 2 }}>
        {template.steps.length === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            У цьому опитувальнику ще немає кроків.
          </Typography>
        ) : (
          <AdaptiveTabs tabs={stepTabs} value={activeStepId} onChange={setActiveStepId} />
        )}

        {!readOnly && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Box>
              {activeStep && (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {activeStep.nestDescriptionI18n?.uk && (
                    <Typography variant="body2" color="text.secondary">{activeStep.nestDescriptionI18n.uk}</Typography>
                  )}
                  <Tooltip title="Редагувати крок">
                    <IconButton size="small" onClick={() => handleOpenEditStep(activeStep)}><Edit fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Видалити крок">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget({ kind: 'step', id: activeStep.id, label: activeStep.nestCaptionI18n?.uk || `Крок ${activeStep.order}` })}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
            <Button size="small" startIcon={<Add />} onClick={handleOpenAddStep}>Крок</Button>
          </Box>
        )}

        {activeStep && (
          <Box sx={{ mt: 2 }}>
            {activeStep.questions.length === 0 ? (
              <Typography color="text.secondary">У цьому кроці ще немає питань.</Typography>
            ) : (
              activeStep.questions.map((question, index) => (
                <QuestionCard
                  key={question.id}
                  question={question}
                  questionTypes={questionTypes}
                  readOnly={readOnly}
                  isFirst={index === 0}
                  isLast={index === activeStep.questions.length - 1}
                  onEdit={() => handleOpenEditQuestion(question)}
                  onDelete={() => setDeleteTarget({ kind: 'question', id: question.id, label: question.nestCaptionI18n?.uk || question.name })}
                  onMoveUp={() => handleMoveQuestion(question, 'up')}
                  onMoveDown={() => handleMoveQuestion(question, 'down')}
                  onAddOption={() => handleOpenAddOption(question.id)}
                  onEditOption={(optionId) => handleOpenEditOption(question, optionId)}
                  onDeleteOption={(optionId) => setDeleteTarget({ kind: 'option', id: optionId, label: 'варіант відповіді' })}
                />
              ))
            )}

            {!readOnly && (
              <Button startIcon={<Add />} onClick={handleOpenAddQuestion} sx={{ mt: 1 }}>
                Питання
              </Button>
            )}
          </Box>
        )}
      </PageCard>

      <StepFormDialog
        open={stepDialogOpen}
        onClose={() => setStepDialogOpen(false)}
        onSave={handleSaveStep}
        editingStep={editingStep}
        nextOrder={nextStepOrder}
      />

      <QuestionFormDialog
        open={questionDialogOpen}
        onClose={() => setQuestionDialogOpen(false)}
        onSave={handleSaveQuestion}
        questionTypes={questionTypes}
        editingQuestion={editingQuestion}
        nextOrder={nextQuestionOrder}
      />

      <OptionFormDialog
        open={optionDialogOpen}
        onClose={() => setOptionDialogOpen(false)}
        onSave={handleSaveOption}
        editingOption={optionTarget?.option ?? null}
        nextOrder={nextOptionOrder}
      />

      <ConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Підтвердження видалення"
        message={`Видалити «${deleteTarget?.label}»? ${deleteTarget?.kind === 'step' ? 'Усі питання й варіанти відповідей цього кроку також буде видалено. ' : ''}Скасувати дію буде неможливо.`}
        confirmText="Видалити"
      />
    </Box>
    </DashboardLayout>
  );
};

const QuizBuilderEditorPage: React.FC = () => (
  <QuizBuilderEditorContent
    service={questionnairesConstructionService}
    iDoc={PageIndices.QUIZ_BUILDER_EDITOR}
    titlePrefix="Конструктор опитувань"
  />
);

export default QuizBuilderEditorPage;
